import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Skeleton } from "antd";
import * as _ from "lodash";
import * as Sentry from "@sentry/react";

import { Swiper, SwiperSlide } from "swiper/react";
// Import Swiper styles
import "swiper/css";
import "swiper/css/free-mode";
import "swiper/css/pagination";

// import required modules
import { Pagination } from "swiper/modules";

import "./styles.css";

import { ModalProductDetail } from "../../featured-products/components/ModalProductDetail";
import { RedTag } from "../../store/components/RedTag";
import { BlueTag } from "../../store/components/BlueTag";
import { OfferTag } from "../../store/components/OfferTag";

import {
  useListaProductosMasVendidosByAddress,
  useListaComunasYMaquinasPorProductoNombre,
} from "@/hooks/useProductos";
import { useListaEmpresasMutation } from "@/hooks/useEmpresas";
import { formatMoney, getDistance, truncateText } from "@/lib";
import { resolverPrecioProducto, resolverBadgesDescuento } from "@/lib/precioProducto";
import { useAuth } from "@/_zustand/auth";

export const LoMasComprado = () => {
  const [show, setShow] = useState(false);
  const empresas = useListaEmpresasMutation();
  const { mutateAsync: empresasMutateAsync } = empresas;
  const [maquinas, setMaquinas] = useState([]);
  const [isLoadingMaquinas, setIsLoadingMaquinas] = useState(false);
  const maxLength = 100;
  let anchoImg = "100%";
  let altoImg = "100%";
  const itemRef = useRef(null);
  const storeRef = useRef(null);
  const requestIdRef = useRef(0);
  const { userAddress } = useAuth();

  const { mutateAsync, isLoading: isLoadingProductos } =
    useListaProductosMasVendidosByAddress();
  const [productosMasVendidos, setProductosMasVendidos] = useState([]);
  const { mutateAsync: mutateAsyncMaquinas } =
    useListaComunasYMaquinasPorProductoNombre();

  const [latitude, setLatitude] = useState(-33.43695899222021);
  const [longitude, setLongitude] = useState(-70.63442929100255);

  const processAndSortProducts = (data, userLat, userLon, getDistance, _) => {
    if (!data || !Array.isArray(data)) return [];

    // 1. Eliminar duplicados primero
    const uniqueData = _.uniqBy(data, "producto_id");

    // 2. Calcular la distancia para cada producto
    const dataWithDistance = uniqueData.map((item) => {
      // Asegurarse de que las coordenadas existan en la máquina
      const maquinaLat = item.maquina_latitud;
      const maquinaLon = item.maquina_longitud;

      const distanciaCalculada =
        maquinaLat && maquinaLon
          ? getDistance(maquinaLat, maquinaLon, userLat, userLon)
          : Infinity; // Usar Infinity para enviar productos sin coordenadas al final

      return {
        ...item,
        _distanciaOrdenamiento: distanciaCalculada,
      };
    });

    // 3. ⭐️ Ordenar de menor a mayor distancia
    return dataWithDistance.sort(
      (a, b) => a._distanciaOrdenamiento - b._distanciaOrdenamiento,
    );
  };

  useEffect(() => {
    // Obtener las coordenadas de usuario, priorizando userAddress
    const currentLat =
      userAddress?.length === 2 ? Number(userAddress[0]) : latitude;
    const currentLon =
      userAddress?.length === 2 ? Number(userAddress[1]) : longitude;

    if (userAddress?.length === 2) {
      mutateAsync({ latitud: currentLat, longitud: currentLon })
        .then((data) => {
          // ⭐️ APLICACIÓN 1: Ordenar los resultados del servidor
          const sortedData = processAndSortProducts(
            data,
            currentLat,
            currentLon,
            getDistance,
            _,
          );
          setProductosMasVendidos(sortedData);
        })
        .catch((error) => console.error("Error al cargar productos:", error));
    } else {
      const success = (position) => {
        setLatitude(position.coords.latitude);
        setLongitude(position.coords.longitude);
      };

      // Opciones para la solicitud de ubicación (opcional)
      const options = {
        enableHighAccuracy: true, // Solicita la mejor precisión posible

        timeout: 5000, // Tiempo máximo en milisegundos para obtener la ubicación

        maximumAge: 0, // No usar una posición en caché, siempre obtener una nueva
      };

      // Sin callback de error explícito: si el usuario niega el permiso o la
      // ubicación no está disponible, se sigue usando la coordenada por
      // defecto ya cargada en latitude/longitude.
      navigator.geolocation.getCurrentPosition(success, undefined, options);

      if (latitude && longitude) {
        mutateAsync({ latitud: currentLat, longitud: currentLon })
          .then((data) => {
            // ⭐️ APLICACIÓN 2: Ordenar después de obtener la ubicación por GPS
            const sortedData = processAndSortProducts(
              data,
              currentLat,
              currentLon,
              getDistance,
              _,
            );
            setProductosMasVendidos(sortedData);
          })
          .catch((error) => console.error("Error al cargar productos:", error));
      } else {
        // Coordenadas por defecto
        const defaultLat = -33.43695899222021;
        const defaultLon = -70.63442929100255;

        mutateAsync({ latitud: defaultLat, longitud: defaultLon })
          .then((data) => {
            // ⭐️ APLICACIÓN 3: Ordenar para la ubicación por defecto
            const sortedData = processAndSortProducts(
              data,
              defaultLat,
              defaultLon,
              getDistance,
              _,
            );
            setProductosMasVendidos(sortedData);
          })
          .catch((error) => console.error("Error al cargar productos:", error));
      }
    }
  }, [userAddress, latitude, longitude, mutateAsync]); // mutateAsync de useMutation es estable entre renders.

  useEffect(() => {
    empresasMutateAsync();
  }, [empresasMutateAsync]);

  const handleClick = async (item) => {
    if (!item) return;
    const requestId = ++requestIdRef.current;
    itemRef.current = item;
    storeRef.current =
      _.find(Array.isArray(empresas.data) ? empresas.data : [], {
        empresa_id: item.empresa_id,
      }) ?? null;
    setMaquinas([]);
    setIsLoadingMaquinas(true);
    setShow(true);
    try {
      const resp = await mutateAsyncMaquinas(item.producto_nombre);
      if (requestIdRef.current !== requestId) return;
      setMaquinas(Array.isArray(resp) ? resp : []);
    } catch (err) {
      if (requestIdRef.current !== requestId) return;
      Sentry.captureException(err);
      setMaquinas(null);
    } finally {
      if (requestIdRef.current === requestId) setIsLoadingMaquinas(false);
    }
  };

  const ProductItem = () => {
    if (_.size(productosMasVendidos) == 0) {
      return (
        <h4 className="text-center">
          No se encontraron productos disponibles.
        </h4>
      );
    }
    if (_.size(productosMasVendidos) < 2)
      return productosMasVendidos.map((item, index) => {
        const { precioFinal, precioOriginal, tieneDescuento } =
          resolverPrecioProducto(item);
        const badges = resolverBadgesDescuento(item);
        return (
        <div
          key={`${item.producto_id}-${item.empresa_id}-${index}`}
          onClick={() => handleClick(item)}
          className="col-12 col-lg-4 mb-5"
        >
          <div
            id={`card_${item.producto_id + item.empresa_id + index}`}
            className="card border-0"
            style={{
              width: "100%",
              backgroundColor:
                "rgba(var(--bs-light-rgb),var(--bs-bg-opacity))!important",
            }}
          >
            <img
              className="border shadow rounded p-2 img-fluid"
              src={item.producto_foto}
              alt={item.producto_id}
              width={anchoImg}
              height={altoImg}
              style={{
                maxHeight: "287px",
                maxWidth: "345px",
                width: "345px",
                height: "287px",
              }}
            />

            <div className="card-body">
              <h6
                className="card-title text-decoration-none text-black montserrat-ultrabold"
                style={{ height: "40px" }}
              >
                {item.producto_nombre}
              </h6>
              <p
                className="montserrat-regular text-black text"
                style={{ height: "80px" }}
              >
                {truncateText(item.producto_descripcion, maxLength)}
              </p>
              <p>{item.genero_nombre}</p>
              <p>{item.marca_nombre}</p>
              {badges.length > 0 && (
                <div className="tags" style={{ marginBottom: "4px" }}>
                  {badges.map((badge, i) =>
                    badge.tipo === "porcentaje" ||
                    badge.tipo === "porcentaje-oferta" ? (
                      <RedTag key={i} percentage={badge.percentage} />
                    ) : badge.tipo === "2x1" ? (
                      <BlueTag key={i} />
                    ) : (
                      <OfferTag key={i} />
                    ),
                  )}
                </div>
              )}
              <div className="d-flex align-items-center justify-content-between">
                <div>
                  <span className="text-black montserrat-ultrabold">
                    {formatMoney(precioFinal)}
                  </span>
                  {tieneDescuento && (
                    <span
                      className="text-muted text-decoration-line-through"
                      style={{ fontSize: "0.8em", display: "block" }}
                    >
                      {formatMoney(precioOriginal)}
                    </span>
                  )}
                </div>
                <div className="btn border rounded-pill border-dark">
                  <i className="fa-solid fa-cart-shopping"></i> Agregar al
                  carrito
                </div>
              </div>
            </div>
          </div>
        </div>
        );
      });
    else
      return (
        <>
          <Swiper
            slidesPerView={3}
            spaceBetween={20}
            grabCursor={true}
            pagination={{
              clickable: true,
            }}
            modules={[Pagination]}
            className="mySwiper"
            breakpoints={{
              340: {
                slidesPerView: 2,
                spaceBetween: 20,
              },
              640: {
                slidesPerView: 2,
                spaceBetween: 20,
              },
              768: {
                slidesPerView: 3,
                spaceBetween: 40,
              },
              1024: {
                slidesPerView: 6,
                spaceBetween: 20,
              },
            }}
          >
            {productosMasVendidos &&
              productosMasVendidos.length > 0 &&
              productosMasVendidos.slice(0, 10).map((item, index) => {
                const { precioFinal, precioOriginal, tieneDescuento } =
                  resolverPrecioProducto(item);
                const badges = resolverBadgesDescuento(item);
                return (
                <SwiperSlide
                  key={`${item.producto_id}-${item.empresa_id}-${index}`}
                >
                  <div
                    className="card-product"
                    onClick={() => handleClick(item)}
                  >
                    <div className="card-image-product">
                      <img src={item.producto_foto} alt={item.producto_id} />
                    </div>
                    <div className="card-content-product">
                      <h2>{item.producto_nombre}</h2>
                      {badges.length > 0 && (
                        <div className="tags" style={{ marginBottom: "4px" }}>
                          {badges.map((badge, i) =>
                            badge.tipo === "porcentaje" ||
                            badge.tipo === "porcentaje-oferta" ? (
                              <RedTag key={i} percentage={badge.percentage} />
                            ) : badge.tipo === "2x1" ? (
                              <BlueTag key={i} />
                            ) : (
                              <OfferTag key={i} />
                            ),
                          )}
                        </div>
                      )}
                      <p className="price-product">
                        {formatMoney(precioFinal)} CLP
                      </p>
                      {tieneDescuento && (
                        <p
                          className="text-muted text-decoration-line-through"
                          style={{ fontSize: "0.8em", margin: 0 }}
                        >
                          {formatMoney(precioOriginal)} CLP
                        </p>
                      )}
                      <div className="location-product">
                        <i className="icon-location"></i>
                        <span className="text-location">
                          {String(item.distancia).slice(0, 4)} km -{" "}
                          {item.maquina_direccion}
                        </span>
                      </div>
                    </div>
                  </div>
                </SwiperSlide>
                );
              })}
          </Swiper>
        </>
      );
  };

  if (productosMasVendidos && productosMasVendidos.length > 0)
    return (
      <div className="container-fluid">
        <section id="productosMasVendidos" className="container my-5">
          <ModalProductDetail
            show={show}
            setShow={setShow}
            item={itemRef}
            store={storeRef}
            machines={maquinas}
            isLoading={isLoadingMaquinas}
          />

          <div className="container my-5" style={{ flexGrow: 1 }}>
            <div className="row">
              <div className="col-12 d-flex justify-content-start align-items-center">
                <div className="col-10">
                  <h3 className="text-start prompt-bold text-black title-20 my-3">
                    ¡Lo más comprado!
                  </h3>
                </div>
                <div className="col-2 text-end">
                  {productosMasVendidos.length > 6 ? (
                    <span>
                      <Link
                        to="/best-seller"
                        className="nav-link active mx-3"
                        aria-current="page"
                      >
                        Ver todos
                      </Link>
                    </span>
                  ) : (
                    <></>
                  )}
                </div>
              </div>

              <div className="row" style={{ height: "fit-content" }}>
                <Skeleton loading={isLoadingProductos} active>
                  <ProductItem />
                </Skeleton>
              </div>
            </div>
          </div>
        </section>
      </div>
    );
};
